import type { AdvisorNotificationEvent } from "@repo/shared/types";
import { useId } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { apiErrorMessage } from "@/lib/api/_client";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "@/lib/hooks/use-notifications";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./page.i18n";

/** The page's text key for each event, in the order the API lists them. */
const EVENT_KEYS = {
  "flow.signature_requested": "signatureRequested",
  "flow.signed": "signed",
  "flow.signature_rejected": "signatureRejected",
  "flow.signature_expired": "signatureExpired",
  "flow.submitted_by_client": "submittedByClient",
  "flow.completed": "completed",
  "flow.compliance_decided": "complianceDecided",
  "support.reply": "supportReply",
} as const satisfies Record<AdvisorNotificationEvent, string>;

/**
 * QL-728 — Settings › Notifications: one opt-in per event, all off until the
 * user turns them on. Each switch saves at once (a partial PUT).
 */
export default function NotificationSettingsPage() {
  const t = useT(T);
  const { data, isError } = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();

  const save = (event: AdvisorNotificationEvent, enabled: boolean) =>
    update.mutate(
      { events: [{ event, enabled }] },
      { onError: (err) => toast.error(apiErrorMessage(err, t.saveFailed)) },
    );

  return (
    <div className="space-y-6">
      <PageHeader title={t.title} description={t.description} />
      <Card>
        <CardContent className="divide-y p-0">
          {isError ? (
            <p className="p-6 text-sm text-destructive">{t.loadFailed}</p>
          ) : !data ? (
            <div className="space-y-3 p-6">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            // Skip an event newer than this build (the API deploys first; tabs stay open for days).
            data.events
              .filter(({ event }) => Object.hasOwn(EVENT_KEYS, event))
              .map(({ event, enabled }) => (
                <PreferenceRow
                  key={event}
                  label={t.events[EVENT_KEYS[event]].label}
                  description={t.events[EVENT_KEYS[event]].description}
                  checked={enabled}
                  disabled={update.isPending}
                  onCheckedChange={(checked) => save(event, checked)}
                />
              ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** One event: its switch, label and one-line description. */
function PreferenceRow({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 px-6 py-4">
      <div className="min-w-0 space-y-1">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <p id={`${id}-description`} className="text-sm text-muted-foreground">
          {description}
        </p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-describedby={`${id}-description`}
      />
    </div>
  );
}
